import { type FormEvent, useCallback, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../hooks/use-auth";
import { t } from "../i18n";

export function LoginPage(): React.JSX.Element {
  const { isAuthenticated, isLoading, error, login, clearError } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = useCallback(
    async (e: FormEvent): Promise<void> => {
      e.preventDefault();
      if (submitting) return;
      setSubmitting(true);
      try {
        await login(email, password);
      } catch {
        // error is set by useAuth
      } finally {
        setSubmitting(false);
      }
    },
    [email, password, submitting, login],
  );

  if (isLoading) {
    return <div style={styles.container}>{t("common.loading")}</div>;
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>{t("login.title")}</h1>
        <p style={styles.subtitle}>{t("login.subtitle")}</p>

        {error && (
          <div style={styles.error} role="alert">
            <span>{error}</span>
            <button
              type="button"
              onClick={clearError}
              style={styles.errorClose}
              aria-label={t("login.dismiss_error")}
            >
              x
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={styles.field}>
            <label htmlFor="email" style={styles.label}>
              {t("login.email")}
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              style={styles.input}
              placeholder="admin@hashee.ai"
            />
          </div>

          <div style={styles.field}>
            <label htmlFor="password" style={styles.label}>
              {t("login.password")}
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              style={styles.input}
            />
          </div>

          <button
            type="submit"
            disabled={submitting || !email || !password}
            style={{
              ...styles.submitBtn,
              ...(submitting ? styles.submitBtnDisabled : {}),
            }}
          >
            {submitting ? t("login.signing_in") : t("login.sign_in")}
          </button>
        </form>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    background: "var(--color-bg-secondary)",
  },
  card: {
    width: "100%",
    maxWidth: 380,
    padding: 32,
    background: "var(--color-bg)",
    border: "1px solid var(--color-border)",
    borderRadius: 8,
  },
  title: {
    fontSize: 20,
    fontWeight: 600,
    marginBottom: 4,
    letterSpacing: "-0.02em",
  },
  subtitle: {
    fontSize: 14,
    color: "var(--color-text-secondary)",
    marginBottom: 24,
  },
  error: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px 12px",
    marginBottom: 16,
    background: "var(--color-danger-bg)",
    border: "1px solid var(--color-danger)",
    borderRadius: 6,
    fontSize: 13,
    color: "var(--color-danger)",
  },
  errorClose: {
    fontSize: 14,
    color: "var(--color-danger)",
    padding: "0 4px",
    lineHeight: 1,
  },
  field: {
    marginBottom: 16,
  },
  label: {
    display: "block",
    fontSize: 13,
    fontWeight: 500,
    marginBottom: 6,
  },
  input: {
    width: "100%",
    padding: "8px 12px",
    border: "1px solid var(--color-border)",
    borderRadius: 6,
    fontSize: 14,
    outline: "none",
  },
  submitBtn: {
    width: "100%",
    padding: "10px 16px",
    fontSize: 14,
    fontWeight: 500,
    borderRadius: 6,
    border: "none",
    background: "var(--color-primary)",
    color: "#ffffff",
    marginTop: 8,
  },
  submitBtnDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
};
